import { z } from "zod";

export const TemplateDocumentTypeSchema = z.enum([
  "INVOICE",
  "CREDIT_NOTE",
  "DEBIT_NOTE",
  "DUNNING",
  "ORDER_CONFIRMATION",
  "DELIVERY_NOTE",
  "PROFORMA",
  "QUOTE",
  "UNKNOWN",
]);

// Define the position of an element (percentage based 0-100)
export const LayoutPositionSchema = z.object({
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  width: z.number().min(0).max(100).optional(),
  height: z.number().min(0).max(100).optional(),
});

export const LayoutElementStateSchema = z.object({
  visible: z.boolean().optional(),
  locked: z.boolean().optional(),
});

export const TemplatePageSchema = z.object({
  id: z.string(),
  name: z.string(),
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
});

// Define color scheme
export const ColorSchemeSchema = z.object({
  primary: z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Color"),
  secondary: z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Color").optional(),
  accent: z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Color").optional(),
  textColor: z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Color").optional(),
  backgroundColor: z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Color").optional(),
});

// Typography settings
export const TypographySchema = z.object({
  headingFont: z.string().default("Helvetica"),
  bodyFont: z.string().default("Helvetica"),
  headingSize: z.number().default(14),
  bodySize: z.number().default(10),
  lineHeight: z.number().default(1.5).optional(),
  // Provenance — were these values extracted from the original PDF, or are they engine defaults?
  detectedFromDocument: z.boolean().optional(),
});

// Define data mapping for specific fields
export const FieldMappingSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  sourceField: z.string(), // e.g. "invoiceNumber", "seller.address"
  fieldType: z.enum([
    "invoiceNumber", "issueDate", "dueDate", "deliveryDate", "customerNumber",
    "sellerName", "sellerAddress", "sellerVatId", "sellerTaxId", "sellerEmail", "sellerPhone", "sellerFax", "sellerWebsite",
    "buyerName", "buyerAddress", "buyerVatId", "buyerReference",
    "lineItemDescription", "lineItemQuantity", "lineItemPrice", "lineItemTotal",
    "totalNet", "totalTax", "totalGross",
    "currency", "paymentTerms", "orderReference",
    "bankName", "iban", "bic", "bankAccount", "bankCode",
    "companyRegistration", "boardMembers",
    "salutation",
    "custom"
  ]).optional().catch(undefined),
  label: z.string().optional(),
  position: LayoutPositionSchema.optional(),
  style: z.object({
    fontFamily: z.string().optional(),
    fontSize: z.number().optional(),
    fontWeight: z.enum(["normal", "bold"]).optional(),
    fontStyle: z.enum(["normal", "italic"]).optional(),
    color: z.string().optional(),
    align: z.enum(["left", "center", "right"]).optional(),
    opacity: z.number().min(0).max(1).optional(),
    borderWidth: z.number().optional(),
    borderColor: z.string().optional(),
  }).optional(),
  // Provenance: where the style came from. 'ai' = matched via sourceIndex; 'spatial' = nearest-item fallback;
  // 'synthesized' = generated from heuristics (e.g. address synthesis); 'user' = user-overridden in editor.
  styleProvenance: z.enum(["ai", "spatial", "synthesized", "user"]).optional(),
  // Confidence the AI assigned to this mapping (0-1).
  confidence: z.number().min(0).max(1).optional(),
  // Page on which this mapping was originally detected (1-based). Defaults to 1.
  pageNumber: z.number().int().min(1).optional(),
}).merge(LayoutElementStateSchema);

// Header section (company info, logo area)
export const HeaderSchema = z.object({
  companyName: z.object({
    position: LayoutPositionSchema.optional(),
    style: z.object({
      fontSize: z.number().optional(),
      fontWeight: z.enum(["normal", "bold"]).optional(),
    }).optional(),
  }).optional(),
  contactInfo: z.object({
    position: LayoutPositionSchema.optional(),
    includePhone: z.boolean().default(true),
    includeEmail: z.boolean().default(true),
    includeWebsite: z.boolean().default(false),
  }).optional(),
});

// Line items table configuration
export const LineItemsTableSchema = z.object({
  position: LayoutPositionSchema.optional(),
  columns: z.array(z.object({
    field: z.enum(["description", "quantity", "unitPrice", "taxRate", "lineTotalAmount"]),
    label: z.string(),
    width: z.number().optional(), // % width
    align: z.enum(["left", "center", "right"]).optional(),
  })).default([
    { field: "description", label: "Description", width: 40, align: "left" },
    { field: "quantity", label: "Qty", width: 10, align: "right" },
    { field: "unitPrice", label: "Price", width: 15, align: "right" },
    { field: "taxRate", label: "Tax %", width: 10, align: "right" },
    { field: "lineTotalAmount", label: "Total", width: 15, align: "right" },
  ]),
  showHeader: z.boolean().default(true),
  alternateRowColors: z.boolean().default(false),
  tableStyle: z.object({
    showGridLines: z.boolean().default(false),
    showOuterBorder: z.boolean().default(true),
    showHeaderSeparator: z.boolean().default(true),
    showRowSeparators: z.boolean().default(false),
    gridColor: z.string().default("#C8C8C8"),
    lineWidth: z.number().default(0.15),
  }).optional(),
}).merge(LayoutElementStateSchema);

export const PaymentMethodSchema = z.object({
  id: z.string().optional(),
  type: z.enum(["BANK_TRANSFER", "PAYPAL", "STRIPE", "OTHER"]),
  label: z.string().min(1),
  details: z.string().optional(), // IBAN, Email, Link
  enabled: z.boolean().default(true),
  position: LayoutPositionSchema.optional(),
  style: z.object({
    fontFamily: z.string().optional(),
    fontSize: z.number().optional(),
    fontWeight: z.enum(["normal", "bold"]).optional(),
    fontStyle: z.enum(["normal", "italic"]).optional(),
    color: z.string().optional(),
    backgroundColor: z.string().optional(),
    borderWidth: z.number().optional(),
    borderColor: z.string().optional(),
    textAlign: z.enum(["left", "center", "right"]).optional(),
    opacity: z.number().min(0).max(1).optional(),
  }).optional(),
});

// Semantic category for classifying static elements
export const SemanticCategorySchema = z.enum([
  "document_title",        // INVOICE, RECHNUNG, CREDIT NOTE, GUTSCHRIFT, MAHNUNG
  "section_heading",       // "Bill To:", "Ship To:", "Payment Details"
  "field_label",           // "Invoice #:", "Date:", "Amount:"
  "legal_boilerplate",     // Terms, conditions, legal notices
  "tax_notice",            // VAT registration, reverse charge notices
  "payment_instruction",   // "Please pay within 30 days", bank transfer instructions
  "company_branding",      // Company name, tagline, slogan
  "contact_info",          // Phone, email, website (static)
  "table_header",          // Column headers for line items
  "decorative",            // Purely visual elements with no semantic meaning
  "unknown"                // Unclassified
]);

// Polygon point schema for complex shapes
export const PolygonPointSchema = z.object({
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
});

// Definition for static elements (background text, lines, polygons, etc.)
export const StaticElementSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  type: z.enum(["text", "rect", "line", "image", "polygon"]),
  content: z.string().optional(), // For text; empty for shapes/lines
  position: LayoutPositionSchema,
  // Polygon-specific: array of vertices (for type: "polygon")
  points: z.array(PolygonPointSchema).optional(),
  closed: z.boolean().optional(), // Whether polygon path is closed
  style: z.object({
    fontFamily: z.string().optional(),
    fontSize: z.number().optional(),
    fontWeight: z.enum(["normal", "bold"]).optional(),
    fontStyle: z.enum(["normal", "italic"]).optional(),
    color: z.string().optional(),
    backgroundColor: z.string().optional(),
    borderWidth: z.number().optional(),
    borderColor: z.string().optional(),
    textAlign: z.enum(["left", "center", "right"]).optional(),
    opacity: z.number().min(0).max(1).optional(),
    // Line-specific styles
    lineWidth: z.number().optional(),
    lineCap: z.enum(["butt", "round", "square"]).optional(),
    lineJoin: z.enum(["miter", "round", "bevel"]).optional(),
  }).optional(),
  zIndex: z.number().default(0),

  // Semantic classification (AI-detected or user-overridden)
  semanticCategory: SemanticCategorySchema.default("unknown"),
  isRepeatable: z.boolean().default(true), // Appears on every document of this type
  relatedFieldIndex: z.number().optional(), // Links a label to its dynamic value mapping index
  pageScope: z.enum(["first", "last", "all", "body"]).default("all"), // Where element appears
  userOverride: z.boolean().default(false), // True if user manually changed the classification
  pageNumber: z.number().int().min(1).optional(), // Page on which the element was originally detected (1-based)
}).merge(LayoutElementStateSchema);

// Detected table region schema
export const DetectedTableRegionSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  rows: z.number(),
  cols: z.number(),
  confidence: z.number().min(0).max(1),
  gridLines: z.object({
    horizontal: z.array(z.number()),
    vertical: z.array(z.number()),
  }).optional(),
});

// Detected repeatable section schema
export const DetectedRepeatableSectionSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  type: z.enum(["header", "footer", "sidebar", "watermark", "pattern"]),
  confidence: z.number().min(0).max(1),
  pageOccurrences: z.array(z.number()),
});

export const TemplatePaginationSchema = z.object({
  kind: z.enum(["content-overflow", "structural-appendix", "mixed"]),
  sourcePageCount: z.number().int().min(1),
  expectedTemplatePagePolicy: z.enum([
    "dynamic-table-single-template",
    "preserve-static-appendix-pages",
    "preserve-overflow-and-appendix-pages",
  ]),
  dynamicArea: z.string().optional(),
  staticAppendixPages: z.number().int().min(0).optional(),
  staticAppendixKinds: z.array(z.string()).optional(),
});

// The main layout configuration schema
export const TemplateLayoutSchema = z.object({
  // Detected document type
  detectedType: TemplateDocumentTypeSchema.default("INVOICE"),
  pages: z.array(TemplatePageSchema).default([
    { id: "page-1", name: "Page 1", widthMm: 210, heightMm: 297 },
  ]),

  // Typography and colors
  colors: ColorSchemeSchema,
  typography: TypographySchema.optional(),
  font: z.string().default("Helvetica"), // Legacy, prefer typography

  // Header section
  header: HeaderSchema.optional(),

  // Specific areas
  logo: z.object({
    position: LayoutPositionSchema,
    url: z.string().url().optional().or(z.literal('')),
    detected: z.boolean().default(false),
  }).merge(LayoutElementStateSchema).optional(),

  addressField: z.object({
    position: LayoutPositionSchema,
    format: z.enum(["DIN_5008_A", "DIN_5008_B", "CUSTOM"]).default("DIN_5008_A"), // Compliance check
  }).merge(LayoutElementStateSchema),

  // Line items table
  lineItemsTable: LineItemsTableSchema.optional(),

  // Totals section
  totals: z.object({
    position: LayoutPositionSchema.optional(),
    showNetAmount: z.boolean().default(true),
    showTaxBreakdown: z.boolean().default(true),
    showGrossAmount: z.boolean().default(true),
  }).merge(LayoutElementStateSchema).optional(),

  // Mapped fields (dynamic)
  mappings: z.array(FieldMappingSchema).default([]),

  // Static Elements for High Fidelity (Background layer)
  staticElements: z.array(StaticElementSchema).default([]),

  // Detected structural elements (from PDF analysis)
  detectedTables: z.array(DetectedTableRegionSchema).optional(),
  detectedRepeatableSections: z.array(DetectedRepeatableSectionSchema).optional(),
  templatePagination: TemplatePaginationSchema.optional(),

  paymentMethods: z.array(PaymentMethodSchema).optional(),

  // Footer content
  footer: z.object({
    position: LayoutPositionSchema.optional(),
    showBankDetails: z.boolean().default(true),
    showTaxIds: z.boolean().default(true),
    customText: z.string().optional(),

    // Bank details layout
    bankDetails: z.object({
      position: LayoutPositionSchema.optional(),
      fields: z.array(z.enum(["bankName", "iban", "bic", "accountHolder"])).default(["iban", "bic"]),
    }).optional(),

    // Columnar layout (legacy: 3 columns max)
    columns: z.object({
      left: z.string().optional(),
      center: z.string().optional(),
      right: z.string().optional(),
    }).optional(),

    // N-column layout (generalized: any number of columns with actual positions)
    footerColumns: z.array(z.object({
      content: z.string(),
      x: z.number(), // % position of column start (0-100)
    })).optional(),
  }).merge(LayoutElementStateSchema).optional(),
});

export type TemplateLayout = z.infer<typeof TemplateLayoutSchema>;
export type TemplateDocumentType = z.infer<typeof TemplateDocumentTypeSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type Typography = z.infer<typeof TypographySchema>;
export type LineItemsTable = z.infer<typeof LineItemsTableSchema>;
export type StaticElement = z.infer<typeof StaticElementSchema>;
export type TemplatePagination = z.infer<typeof TemplatePaginationSchema>;
export type TemplatePage = z.infer<typeof TemplatePageSchema>;
export type SemanticCategory = z.infer<typeof SemanticCategorySchema>;
export type DetectedTableRegion = z.infer<typeof DetectedTableRegionSchema>;
export type DetectedRepeatableSection = z.infer<typeof DetectedRepeatableSectionSchema>;
export type PolygonPoint = z.infer<typeof PolygonPointSchema>;

